import { FormField, FormNameEnum, useMutationNotifier } from '@alice-financial/alice-sdk'
import { GqlApiError } from '@alice-financial/api'
import {
  ActionLayout,
  Box,
  Button,
  Carousel,
  LinearProgress,
  useActiveSlide,
} from '@alice-financial/pretext-ui'
import { UseMutateAsyncFunction } from '@tanstack/react-query'
import * as React from 'react'
import { useForm } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { useCurrentOrgRootPath } from '../orgDashboard/organization/useOrg'
import {
  CreateFormResponseVariables,
  UseCreateFormResponseMutationOptions,
  type CreateFormResponseMutation,
} from '../orgDashboard/survey/useCreateFormResponse'
import { FormStatus, Question, QuestionPage, SurveyFieldValues } from './types'
import { parseFormInputs, useValuesFromApi } from './formValuesUtils'

const isFileUploadQuestion = (question: Question) => 'fileUpload' in question

type PagedFormProps = {
  formName: FormNameEnum
  formVersion: number
  formFields: FormField[] | undefined
  pages: readonly QuestionPage[]
  onCompleteReturnTo?: string // optional path to redirect to after form is submitted, relative to current org root path
  createFormResponse: UseMutateAsyncFunction<
    CreateFormResponseMutation,
    GqlApiError<CreateFormResponseVariables>,
    CreateFormResponseVariables
  >
  disableDone?: boolean
  status?: FormStatus
}
export const PagedForm = ({
  formName,
  formVersion,
  formFields,
  pages,
  onCompleteReturnTo,
  createFormResponse,
  disableDone,
  status,
}: PagedFormProps) => {
  const isStarted = status !== 'notStarted'
  const currentOrgRootPath = useCurrentOrgRootPath()
  const allQuestions: Question[] = React.useMemo(() => pages.flatMap((page) => page.questions), [pages])
  // use a memoized key to track the question ids to force remount when questions change (allows carousel layout to be updated)
  const questionsDefKey = React.useMemo(
    () => allQuestions.map(({ questionId }) => questionId).join(),
    [allQuestions]
  )

  const [activeStep] = useActiveSlide()
  const navigate = useNavigate()
  // For manual carousel's slide height updates
  const carouselActionsRef = React.useRef<{ updateHeight: () => void } | null>(null)

  const values = useValuesFromApi(formFields, pages)
  const {
    handleSubmit,
    register,
    control,
    formState: { isDirty },
  } = useForm<SurveyFieldValues>({ values })
  const getSubmit = (mutationOptions?: UseCreateFormResponseMutationOptions) =>
    handleSubmit((values) => {
      createFormResponse(
        parseFormInputs(allQuestions, values) as CreateFormResponseVariables,
        mutationOptions
      )
    })

  const onCompleteMutationOptions: UseCreateFormResponseMutationOptions = useMutationNotifier(
    {},
    {
      onSuccess: () => {
        const returnTo = onCompleteReturnTo
          ? `${currentOrgRootPath}/${onCompleteReturnTo}`
          : currentOrgRootPath
        navigate(returnTo)
      },
    }
  )
  const onSubmit = getSubmit()
  const onComplete = getSubmit(onCompleteMutationOptions)

  // single page version - no carousel
  if (pages.length === 1 && pages[0]) {
    const Page = pages[0].Page
    return (
      <>
        <Page register={register} control={control} formName={formName} formVersion={formVersion} />
        {allQuestions.filter((q) => !isFileUploadQuestion(q)).length > 0 && (
          <ActionLayout
            primary={
              <Button variant="contained" onClick={onComplete}>
                Done
              </Button>
            }
          />
        )}
      </>
    )
  }

  const previousPage = pages[activeStep - 1]
  const hasPreviousPageWithQuestions = previousPage && previousPage.questions.length > 0
  const nextPage = pages[activeStep + 1]
  const hasNextPageWithQuestions = nextPage && nextPage.questions.length > 0
  const activePage = pages[activeStep]
  const activePageHasQuestions = activePage && activePage.questions.length > 0
  const backButtonLabel = activePageHasQuestions ? (
    <FormattedMessage id="common.back" />
  ) : hasPreviousPageWithQuestions ? (
    isStarted ? (
      <FormattedMessage id="paged_form.edit_survey" />
    ) : (
      <FormattedMessage id="paged_form.start_survey" />
    )
  ) : undefined
  const nextButtonLabel = activePageHasQuestions ? (
    <FormattedMessage id="common.next" />
  ) : hasNextPageWithQuestions ? (
    isStarted ? (
      <FormattedMessage id="paged_form.edit_survey" />
    ) : (
      <FormattedMessage id="paged_form.start_survey" />
    )
  ) : undefined
  return (
    <>
      <Box mb={2} width="100%" marginX="auto" bgcolor="#ddd">
        <LinearProgress variant="determinate" value={((activeStep + 1) / pages.length) * 100} />
      </Box>
      <form onSubmit={onSubmit}>
        <Carousel
          key={questionsDefKey} // force remount when questions change
          action={(actions) => {
            // Trigger initial height update on mount/remount
            actions.updateHeight()
            // Store carousel actions for manual height updates (for the components
            // not implementing questions, e.g. no dynamic `questionsDefKey`)
            carouselActionsRef.current = actions
          }}
          animateHeight
          onStepChange={() => isDirty && onSubmit()}
          onComplete={onComplete}
          mouseOnly
          disableDone={disableDone}
          backButtonLabel={backButtonLabel}
          nextButtonLabel={nextButtonLabel}
        >
          {pages.map((page, index) => (
            <Box key={index} pb={2}>
              <page.Page
                register={register}
                control={control}
                formName={formName}
                formVersion={formVersion}
                // Pass `updateHeight` to Page for manual calls
                triggerHeightUpdate={carouselActionsRef.current?.updateHeight}
              />
            </Box>
          ))}
        </Carousel>
      </form>
    </>
  )
}
